// Localização: frontend/src/components/Financeiro/ModalPagamento.jsx
// Modal para registrar pagamento total ou parcial

import React, { useState, useEffect, useRef } from 'react';
import Modal from '../UI/Modal';
import PrimaryButton from '../Common/PrimaryButton';
import LoadingIndicator from '../Common/LoadingIndicator';
import { useContasBancarias } from '../../hooks/useFinanceiro';
import { DollarSign, CreditCard, Plus, Minus } from 'lucide-react';

const ModalPagamento = ({ isOpen, onClose, conta, onConfirm, isLoading }) => {
    const [tipoPagamento, setTipoPagamento] = useState('total'); // 'total' ou 'parcial'
    const [valorParcial, setValorParcial] = useState('');
    const [dataPagamento, setDataPagamento] = useState(new Date().toISOString().split('T')[0]);
    const [formaPagamento, setFormaPagamento] = useState(conta?.conta_bancaria_id || conta?.conta_bancaria?.id || conta?.forma_pagamento || '');
    const [juros, setJuros] = useState('');
    const [multa, setMulta] = useState('');
    const [errors, setErrors] = useState({});
    const hasLoadedContasRef = useRef(false);

    // Hook para buscar contas bancárias
    const { contas, fetchContas, loading: loadingContas } = useContasBancarias();

    useEffect(() => {
        if (isOpen) {
            // Carregar contas apenas uma vez quando o modal abrir
            if (!hasLoadedContasRef.current || contas.length === 0) {
                fetchContas().catch(err => {
                    console.error('[ModalPagamento] Erro ao carregar contas bancárias:', err);
                });
                hasLoadedContasRef.current = true;
            }

            if (conta) {
                setFormaPagamento(conta.conta_bancaria_id || conta.conta_bancaria?.id || conta.forma_pagamento || '');
                setDataPagamento(new Date().toISOString().split('T')[0]);
                setValorParcial('');
                setJuros('');
                setMulta('');
                setTipoPagamento('total');
                setErrors({});
            }
        } else {
            // Resetar flag quando o modal fechar
            hasLoadedContasRef.current = false;
        }
    }, [conta, isOpen]); // Removido fetchContas das dependências

    const formatarMoeda = (valor) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor);
    };

    // Calcular valor total a pagar
    const calcularValorTotal = () => {
        const valorBase = tipoPagamento === 'total'
            ? parseFloat(conta?.valor_pendente || 0)
            : parseFloat(valorParcial || 0);
        const valorJuros = parseFloat(juros || 0);
        const valorMulta = parseFloat(multa || 0);
        return valorBase + valorJuros + valorMulta;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const newErrors = {};

        if (!dataPagamento) {
            newErrors.dataPagamento = 'Data de pagamento é obrigatória';
        }

        if (!formaPagamento) {
            newErrors.formaPagamento = 'Conta bancária é obrigatória';
        }

        if (tipoPagamento === 'parcial') {
            if (!valorParcial || parseFloat(valorParcial) <= 0) {
                newErrors.valorParcial = 'Valor deve ser maior que zero';
            } else if (parseFloat(valorParcial) > parseFloat(conta.valor_pendente)) {
                newErrors.valorParcial = `Valor não pode ser maior que o pendente (${formatarMoeda(conta.valor_pendente)})`;
            }
        }

        // Validar juros e multa
        if (juros && parseFloat(juros) < 0) {
            newErrors.juros = 'Juros não podem ser negativos';
        }
        if (multa && parseFloat(multa) < 0) {
            newErrors.multa = 'Multa não pode ser negativa';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        const data = {
            data_pagamento: dataPagamento,
            forma_pagamento: null, // Removido - agora usa conta_bancaria_id
            conta_bancaria_id: formaPagamento || null,
            juros: parseFloat(juros || 0),
            multa: parseFloat(multa || 0)
        };

        if (tipoPagamento === 'parcial') {
            data.valor = parseFloat(valorParcial);
        }

        onConfirm(data);
    };

    if (!conta) return null;

    const valorPendente = parseFloat(conta.valor_pendente);
    const valorTotal = calcularValorTotal();

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Registrar Pagamento">
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Informações da conta */}
                <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">{conta.descricao}</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <p className="text-gray-600">Valor Total</p>
                            <p className="font-semibold text-gray-900">{formatarMoeda(conta.valor_total)}</p>
                        </div>
                        <div>
                            <p className="text-gray-600">Valor Pago</p>
                            <p className="font-semibold text-green-600">{formatarMoeda(conta.valor_pago)}</p>
                        </div>
                        <div>
                            <p className="text-gray-600">Valor Pendente</p>
                            <p className="font-semibold text-orange-600">{formatarMoeda(valorPendente)}</p>
                        </div>
                        <div>
                            <p className="text-gray-600">Vencimento</p>
                            <p className="font-semibold text-gray-900">
                                {new Date(conta.data_vencimento).toLocaleDateString('pt-BR')}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Tipo de pagamento */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tipo de Pagamento
                    </label>
                    <div className="flex gap-4">
                        <label className="flex items-center">
                            <input
                                type="radio"
                                value="total"
                                checked={tipoPagamento === 'total'}
                                onChange={(e) => setTipoPagamento(e.target.value)}
                                className="mr-2"
                            />
                            <span>Pagamento Total ({formatarMoeda(valorPendente)})</span>
                        </label>
                        <label className="flex items-center">
                            <input
                                type="radio"
                                value="parcial"
                                checked={tipoPagamento === 'parcial'}
                                onChange={(e) => setTipoPagamento(e.target.value)}
                                className="mr-2"
                            />
                            <span>Pagamento Parcial</span>
                        </label>
                    </div>
                </div>

                {/* Valor parcial */}
                {tipoPagamento === 'parcial' && (
                    <div>
                        <label htmlFor="valorParcial" className="block text-sm font-medium text-gray-700 mb-1">
                            Valor do Pagamento <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="number"
                            id="valorParcial"
                            value={valorParcial}
                            onChange={(e) => {
                                setValorParcial(e.target.value);
                                if (errors.valorParcial) {
                                    setErrors(prev => ({ ...prev, valorParcial: null }));
                                }
                            }}
                            step="0.01"
                            min="0.01"
                            max={valorPendente}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.valorParcial ? 'border-red-500' : 'border-gray-300'
                                }`}
                            placeholder={`Máximo: ${formatarMoeda(valorPendente)}`}
                        />
                        {errors.valorParcial && (
                            <p className="mt-1 text-sm text-red-600">{errors.valorParcial}</p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">
                            Valor restante após pagamento: {formatarMoeda(
                                valorParcial ? valorPendente - parseFloat(valorParcial) : valorPendente
                            )}
                        </p>
                    </div>
                )}

                {/* Juros e Multa */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="juros" className="block text-sm font-medium text-gray-700 mb-1">
                            <Plus className="inline h-3 w-3 mr-1" />
                            Juros (R$)
                        </label>
                        <input
                            type="number"
                            id="juros"
                            value={juros}
                            onChange={(e) => {
                                setJuros(e.target.value);
                                if (errors.juros) {
                                    setErrors(prev => ({ ...prev, juros: null }));
                                }
                            }}
                            step="0.01"
                            min="0"
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.juros ? 'border-red-500' : 'border-gray-300'
                                }`}
                            placeholder="0,00"
                        />
                        {errors.juros && (
                            <p className="mt-1 text-sm text-red-600">{errors.juros}</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="multa" className="block text-sm font-medium text-gray-700 mb-1">
                            <Minus className="inline h-3 w-3 mr-1" />
                            Multa (R$)
                        </label>
                        <input
                            type="number"
                            id="multa"
                            value={multa}
                            onChange={(e) => {
                                setMulta(e.target.value);
                                if (errors.multa) {
                                    setErrors(prev => ({ ...prev, multa: null }));
                                }
                            }}
                            step="0.01"
                            min="0"
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.multa ? 'border-red-500' : 'border-gray-300'
                                }`}
                            placeholder="0,00"
                        />
                        {errors.multa && (
                            <p className="mt-1 text-sm text-red-600">{errors.multa}</p>
                        )}
                    </div>
                </div>

                {/* Resumo do valor total */}
                {(juros || multa) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="text-sm space-y-1">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Valor base:</span>
                                <span className="font-medium">{formatarMoeda(
                                    tipoPagamento === 'total' ? valorPendente : parseFloat(valorParcial || 0)
                                )}</span>
                            </div>
                            {juros && (
                                <div className="flex justify-between">
                                    <span className="text-gray-600">+ Juros:</span>
                                    <span className="font-medium">{formatarMoeda(parseFloat(juros))}</span>
                                </div>
                            )}
                            {multa && (
                                <div className="flex justify-between">
                                    <span className="text-gray-600">+ Multa:</span>
                                    <span className="font-medium">{formatarMoeda(parseFloat(multa))}</span>
                                </div>
                            )}
                            <div className="flex justify-between pt-2 border-t border-blue-300">
                                <span className="font-semibold text-gray-900">Total a Pagar:</span>
                                <span className="font-bold text-emerald-600 text-lg">{formatarMoeda(valorTotal)}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Data de pagamento */}
                <div>
                    <label htmlFor="dataPagamento" className="block text-sm font-medium text-gray-700 mb-1">
                        Data de Pagamento <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="date"
                        id="dataPagamento"
                        value={dataPagamento}
                        onChange={(e) => {
                            setDataPagamento(e.target.value);
                            if (errors.dataPagamento) {
                                setErrors(prev => ({ ...prev, dataPagamento: null }));
                            }
                        }}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.dataPagamento ? 'border-red-500' : 'border-gray-300'
                            }`}
                    />
                    {errors.dataPagamento && (
                        <p className="mt-1 text-sm text-red-600">{errors.dataPagamento}</p>
                    )}
                </div>

                {/* Forma de Pagamento (Conta Bancária) */}
                <div>
                    <label htmlFor="formaPagamento" className="block text-sm font-medium text-gray-700 mb-1">
                        <CreditCard className="inline h-4 w-4 mr-1" />
                        Forma de Pagamento (Conta Bancária) <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <select
                            id="formaPagamento"
                            value={formaPagamento}
                            onChange={(e) => {
                                setFormaPagamento(e.target.value);
                                if (errors.formaPagamento) {
                                    setErrors(prev => ({ ...prev, formaPagamento: null }));
                                }
                            }}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.formaPagamento ? 'border-red-500' : 'border-gray-300'
                                }`}
                            disabled={loadingContas}
                        >
                            <option value="">Selecione uma conta bancária...</option>
                            {contas.map(contaBancaria => (
                                <option key={contaBancaria.id} value={contaBancaria.id}>
                                    {contaBancaria.nome} - {contaBancaria.banco} {contaBancaria.agencia ? `(${contaBancaria.agencia})` : ''}
                                </option>
                            ))}
                        </select>
                        {loadingContas && (
                            <div className="absolute right-3 top-2.5">
                                <LoadingIndicator size="sm" />
                            </div>
                        )}
                    </div>
                    {errors.formaPagamento && (
                        <p className="mt-1 text-sm text-red-600">{errors.formaPagamento}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                        Selecione a conta bancária de onde o pagamento será realizado
                    </p>
                </div>

                {/* Botões */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                        Cancelar
                    </button>
                    <PrimaryButton
                        type="submit"
                        disabled={isLoading}
                        className="px-4 py-2 flex items-center gap-2"
                    >
                        <DollarSign className="h-4 w-4" />
                        {isLoading ? 'Registrando...' : 'Confirmar Pagamento'}
                    </PrimaryButton>
                </div>
            </form>
        </Modal>
    );
};

export default ModalPagamento;
